import API, { State, User, Vote } from "./API.js"
import Data, { Entry } from "./Data.js"
import Display from "./Display.js"
import EurovisionworldAPI, { CountryCode } from "./EurovisionworldAPI.js"
import YoutubeAPI, { Playlists } from "./YoutubeAPI.js"

export default class SongContest {
	static updateInterval = 10_000

	users: string[]
	countries: { [name: string]: CountryCode }
	eurovisionworldAPI: EurovisionworldAPI
	data: Data
	dataSemi1: Data
	dataSemi2: Data
	display: Display
	displaySemi1: Display
	displaySemi2: Display
	youtubeAPI?: YoutubeAPI
	user?: string
	state: State
	private intervalID: number

	constructor(finalTable: HTMLTableElement, semifinals1Table: HTMLTableElement, semifinals2Table: HTMLTableElement, { year, ongoing, voting }: State, youtubeAPIKey?: string, playlists?: Playlists) {
		this.state = { year, ongoing, voting }
		this.eurovisionworldAPI = new EurovisionworldAPI(year)
		if (youtubeAPIKey != undefined) this.youtubeAPI = new YoutubeAPI(youtubeAPIKey, year, playlists)

		this.display = new Display({
			table: finalTable,
			sortBy: "nr",
			primaryKey: "country",
		})

		this.displaySemi1 = new Display({
			table: semifinals1Table,
			sortBy: "nr",
			primaryKey: "country",
		})

		this.displaySemi2 = new Display({
			table: semifinals2Table,
			sortBy: "nr",
			primaryKey: "country",
		})
		
		const sort = (by, descending) => {
			this.display.sortBy = by
			this.display.descending = descending
			this.display.data.sort(by, descending)
			this.display.show()
			
			this.displaySemi1.sortBy = by
			this.displaySemi1.descending = descending
			this.displaySemi1.data.sort(by, descending)
			this.displaySemi1.show()
			
			this.displaySemi2.sortBy = by
			this.displaySemi2.descending = descending
			this.displaySemi2.data.sort(by, descending)
			this.displaySemi2.show()
		}
		
		// Synchronize sorting
		this.display.sort = (by = this.display.sortBy, descending = this.display.descending) =>
			sort(by, descending)
		
		this.displaySemi1.sort = (by = this.displaySemi1.sortBy, descending = this.displaySemi1.descending) =>
			sort(by, descending)
		
		this.displaySemi2.sort = (by = this.displaySemi2.sortBy, descending = this.displaySemi2.descending) =>
			sort(by, descending)
	}

	async init() {
		const votesPromise = API.getVotes(this.state.year)
		const countriesPromise = fetch("data/country-codes.json").then(_ => _.json())
		const eurovisionworldPromise = this.eurovisionworldAPI.init()
		const youtubeAPIPromise = this.youtubeAPI?.init()

		this.users ??= await API.getUsers().then(users => users.map(user => user.toLowerCase()))
		this.countries = await countriesPromise
		await eurovisionworldPromise

		this.data = new Data({
			entries: this.eurovisionworldAPI.getFinalEntries()
				.map(entry => ({ ...entry, country: this.countryNameFromCode(entry.country) })),
			inputColumns: [this.user?.toLowerCase()],
			columns: ["nr", "country", "song", "artist", "views", "likes", "place", ...this.users, "average"],
		})

		this.dataSemi1 = new Data({
			entries: this.eurovisionworldAPI.getSemifinal1Entries()
				.map(entry => ({ ...entry, country: this.countryNameFromCode(entry.country) })),
			inputColumns: [this.user?.toLowerCase()],
			columns: ["nr", "country", "song", "artist", "views", "likes", "place", ...this.users, "average"],
		})

		this.dataSemi2 = new Data({
			entries: this.eurovisionworldAPI.getSemifinal2Entries()
				.map(entry => ({ ...entry, country: this.countryNameFromCode(entry.country) })),
			inputColumns: [this.user?.toLowerCase()],
			columns: ["nr", "country", "song", "artist", "views", "likes", "place", ...this.users, "average"],
		})

		this.display.getImageURL = this.onGetImageURL.bind(this)
		this.display.addValue = this.onAddValue.bind(this)
		this.display.updateValue = this.onUpdateValue.bind(this)

		this.displaySemi1.getImageURL = this.onGetImageURL.bind(this)
		this.displaySemi1.addValue = this.onAddValue.bind(this)
		this.displaySemi1.updateValue = this.onUpdateValue.bind(this)

		this.displaySemi2.getImageURL = this.onGetImageURL.bind(this)
		this.displaySemi2.addValue = this.onAddValue.bind(this)
		this.displaySemi2.updateValue = this.onUpdateValue.bind(this)

		await youtubeAPIPromise
		this.display.init(this.data)
		this.displaySemi1.init(this.dataSemi1)
		this.displaySemi2.init(this.dataSemi2)

		// Get sorting from url params, if present
		const params = (new URL(document.location as any)).searchParams
		let sortBy = params.get("sort") ?? this.display.sortBy
		let order = params.get("order")
		let descending = order ? order.startsWith("d") : this.display.descending
		this.display.sort(sortBy, descending)
		this.displaySemi1.sort(sortBy, descending)
		this.displaySemi2.sort(sortBy, descending)

		this.highlightCurrentUser()
		this.updateVideoStats()
		this.updateVotes(await votesPromise)

		this.startUpdateInterval(false)
		document.addEventListener("visibilitychange", () => document.hidden
			? clearInterval(this.intervalID)
			: this.startUpdateInterval(true))
	}

	onGetImageURL(_, entry) {
		return `https://flagcdn.com/w80/${this.countries[entry.country]}.webp`
	}

	onAddValue(td, entry, entryName, column) {
		const options = ["", 1, 2, 3, 4, 5, 6, 7, 8, 10, 12]
		if (this.data.data.inputColumns.includes(column)
				|| this.dataSemi1.data.inputColumns.includes(column)
				|| this.dataSemi2.data.inputColumns.includes(column)) {
			const selectElem = document.createElement("select")
			selectElem.setAttribute("column", column)
			selectElem.setAttribute("entry", entryName)
			selectElem.toggleAttribute("disabled", this.state.voting == false)
			selectElem.onchange = (e) =>
				this.onInputChage(entry, column, Number((e.target as HTMLInputElement).value))
			td.append(selectElem)
			for (const option of options) {
				const optionElem = document.createElement("option")
				optionElem.setAttribute("value", option as any)
				optionElem.innerText = option as any
				selectElem.append(optionElem)
			}
		}
	}

	onUpdateValue(td, entry, entryName, column, value) {
		const inputElem = td.getElementsByTagName("select")[0]
		if (inputElem) {
			inputElem.value = value
		} else {
			td.innerText = (value == 0)
				? ""
				: isNaN(value) ? value : Number(value).toLocaleString()
		}

		if (column == "song" && value != "" && this.youtubeAPI != undefined) {
			const videoId = this.youtubeAPI.getVideoID(entry.country)
			if (videoId != undefined) {
				const linkElem = document.createElement("a")
				linkElem.innerText = value
				linkElem.href = YoutubeAPI.getURLFromVideoID(videoId)
				td.replaceChildren(linkElem)
			}
		}
	}

	onInputChage(entry: Entry, column: string, value: number) {
		entry[column] = value
		API.setVote(this.state.year, this.user, this.countries[entry.country], value)
		this.checkErrors()
	}

	markErrors(column: string, display: Display, entries: Entry[]) {
		for (const entry of entries) {
			const row = display.rows[entry[Data.idxSymbol]]
			row.querySelector(`td[column="${column}"]`).classList.add("error")
		}
	}

	checkErrors() {
		this.display.table.querySelectorAll(".error").forEach(elem => elem.classList.remove("error"))
		this.displaySemi1.table.querySelectorAll(".error").forEach(elem => elem.classList.remove("error"))
		this.displaySemi2.table.querySelectorAll(".error").forEach(elem => elem.classList.remove("error"))

		// Check duplicates
		const markDuplicates = (data: Data, display: Display) => {
			for (const user of this.users) {
				// Map from scores to entries with that score
				const votes = Map.groupBy(
					data.entries.filter(entry => entry[user] != undefined && entry[user] != 0),
					entry => entry[user])
				
				votes.values()
					.filter(entries => entries.length > 1)
					.forEach(entries => this.markErrors(user, display, entries))
			}
		}
		markDuplicates(this.data, this.display)
		markDuplicates(this.dataSemi1, this.displaySemi1)
		markDuplicates(this.dataSemi2, this.displaySemi2)

		// Check options
		const validOptions = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12]
		const markInvalidScores = (data: Data, display: Display) => {
			for (const user in this.users) {
				const entries = data.entries.filter(entry => entry[user] && !validOptions.includes(entry[user]))
				this.markErrors(user, display, entries)
			}
		}
		markInvalidScores(this.data, this.display)
		markInvalidScores(this.dataSemi1, this.displaySemi1)
		markInvalidScores(this.dataSemi2, this.displaySemi2)
	}

	highlightCurrentUser() {
		if (this.user == undefined) return
		for (const td of this.display.table.querySelectorAll(`td[column=${this.user.toLowerCase()}]`)) {
			td.classList.add("currentUser")
		}
		for (const td of this.displaySemi1.table.querySelectorAll(`td[column=${this.user.toLowerCase()}]`)) {
			td.classList.add("currentUser")
		}
		for (const td of this.displaySemi2.table.querySelectorAll(`td[column=${this.user.toLowerCase()}]`)) {
			td.classList.add("currentUser")
		}
	}

	startUpdateInterval(updateNow: boolean) {
		if (updateNow && this.state.ongoing) this.update()
		clearInterval(this.intervalID)
		if (SongContest.updateInterval > 0 && this.state.ongoing) {
			this.intervalID = setInterval(this.update.bind(this), SongContest.updateInterval)
		}
	}

	updateVotes(votes: Vote[]) {
		for (const vote of votes) {
			if (vote.score == 0) continue
			const entry = this.data.entries.find((entry: Entry) => this.countries[entry.country] == vote.country)
				?? this.dataSemi1.entries.find((entry: Entry) => this.countries[entry.country] == vote.country)
				?? this.dataSemi2.entries.find((entry: Entry) => this.countries[entry.country] == vote.country)
			if (entry) entry[vote.user.toLowerCase()] = vote.score
		}
		this.updateAverage()
		this.display.updateData()
		this.displaySemi1.updateData()
		this.displaySemi2.updateData()
		this.checkErrors()
	}

	updateVideoStats() {
		for (const [country, video] of this.youtubeAPI.videos.entries()) {
			if (video.views == undefined && video.likes == undefined) continue
			const entry = this.data.entries.find((entry: Entry) => entry.country.toLowerCase() == country)
				?? this.dataSemi1.entries.find((entry: Entry) => entry.country.toLowerCase() == country)
				?? this.dataSemi2.entries.find((entry: Entry) => entry.country.toLowerCase() == country)
			if (entry) {
				entry.views = video.views
				entry.likes = video.likes
			}
		}
		this.display.updateData()
		this.displaySemi1.updateData()
		this.displaySemi2.updateData()
	}

	updateAverage() {
		const updateAverageFor = (data: Data) => {
			for (const entry of data.entries) {
				let totalPoints = 0
				for (const user of this.users) {
					if (entry[user] != undefined) totalPoints += entry[user]
				}
				entry.average = Math.round(totalPoints / this.users.length * 10) / 10
			}
		}
		
		updateAverageFor(this.data)
		updateAverageFor(this.dataSemi1)
		updateAverageFor(this.dataSemi2)
	}

	update() {
		API.getState(this.state.year).then(state => {
			this.state = state
			this.toggleReadonly(this.state.voting == false)
		})
		API.getVotes(this.state.year).then(this.updateVotes.bind(this))
	}

	async login(name: string) {
		let users = await API.getUsers()
		this.users = users.map(user => user.toLowerCase())
		this.user = users.find(user => user == name)
		return this.user
	}

	countryNameFromCode(code: CountryCode) {
		for (const name in this.countries) {
			if (this.countries[name] == code) return name
		}
	}

	toggleReadonly(readonly: boolean) {
		for (const elem of this.display.table.getElementsByTagName("select")) {
			elem.toggleAttribute("disabled", readonly)
		}
		for (const elem of this.displaySemi1.table.getElementsByTagName("select")) {
			elem.toggleAttribute("disabled", readonly)
		}
		for (const elem of this.displaySemi2.table.getElementsByTagName("select")) {
			elem.toggleAttribute("disabled", readonly)
		}
	}

	async setYear(year: number) {
		const statePromise = API.getState(year)
		const votesPromise = API.getVotes(year)

		this.eurovisionworldAPI.year = year
		this.youtubeAPI.year = year

		await Promise.all([this.eurovisionworldAPI.init(), this.youtubeAPI.init()])

		this.data.setData({
			...this.data.data,
			entries: this.eurovisionworldAPI.getFinalEntries()
				.map(entry => ({ ...entry, country: this.countryNameFromCode(entry.country) })),
		})

		this.dataSemi1.setData({
			...this.dataSemi1.data,
			entries: this.eurovisionworldAPI.getSemifinal1Entries()
				.map(entry => ({ ...entry, country: this.countryNameFromCode(entry.country) })),
		})

		this.dataSemi2.setData({
			...this.dataSemi2.data,
			entries: this.eurovisionworldAPI.getSemifinal2Entries()
				.map(entry => ({ ...entry, country: this.countryNameFromCode(entry.country) })),
		})

		this.display.init(this.data)
		this.displaySemi1.init(this.dataSemi1)
		this.displaySemi2.init(this.dataSemi2)

		this.updateVideoStats()
		this.updateVotes(await votesPromise)
		this.display.sort()
		this.displaySemi1.sort()
		this.displaySemi2.sort()
		this.highlightCurrentUser()
		this.state = await statePromise
		this.toggleReadonly(this.state.voting == false)
	}

}
